import React from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Card, CardContent, Divider } from "@mui/material";

// Icons
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReplyIcon from "@mui/icons-material/Reply";
import CallIcon from "@mui/icons-material/Call";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";

/**
 * TeamplateSendPreview
 * Renders a preview of the selected template (header, body, footer, and buttons).
 */
const TeamplateSendPreview = () => {
  // Example: Adjust this selector based on where your selectedTemplate is stored
  const { selectedTemplate } = useSelector((state) => state.template);

  if (!selectedTemplate || !selectedTemplate.components) return null;

  // Find each component by type
  const headerComp = selectedTemplate.components.find(
    (comp) => comp.type === "HEADER"
  );
  const bodyComp = selectedTemplate.components.find(
    (comp) => comp.type === "BODY"
  );
  const footerComp = selectedTemplate.components.find(
    (comp) => comp.type === "FOOTER"
  );
  const buttonsComp = selectedTemplate.components.find(
    (comp) => comp.type === "BUTTONS"
  );
  const carouselComp = selectedTemplate.components.find(
    (comp) => comp.type?.toLowerCase() === "carousel"
  );

  // Determine header format (TEXT, IMAGE, VIDEO, DOCUMENT, etc.)
  const headerFormat = headerComp?.format;

  // Basic text for header, body, and footer
  const headerText = headerComp?.text;
  const bodyText = bodyComp?.text;
  const footerText = footerComp?.text;

  // Buttons array (each button has a type, e.g. QUICK_REPLY, PHONE, LINK, COPY_CODE, etc.)
  const buttonsData = buttonsComp?.buttons || [];

  // Carousel data
  const carouselCards = carouselComp?.cards || [];

  return (
    <Box width={"100%"} display={"flex"} justifyContent={"center"}>
      <Card
        sx={{
          width: '100%',
          maxWidth: { xs: "100%", sm: 280, md: 300, lg: 300, xl: 300 },
          borderRadius: "8px",
          border: "1px solid",
          borderColor: (theme) => theme.palette.divider,
          backgroundColor: (theme) => theme.palette.background.paper,
          padding: 0,
          mt: { xs: 1, sm: 3, md: 4, lg: 5, xl: 5 },
        }}
      >
        <CardContent sx={{ padding: 0 }}>
          {/* Header */}
          {headerComp && headerFormat === "TEXT" && (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ 
                padding: { xs: "6px 0 0 6px", sm: "8px 0 0 8px", md: "10px 0 0 10px", lg: "10px 0 0 10px", xl: "10px 0 0 10px" },
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "16px", xl: "16px" }
              }}
            >
              {headerText}
            </Typography>
          )}

          {headerComp && headerFormat === "IMAGE" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                borderBottom: "1px solid rgb(219, 219, 219)",
              }}
            >
              <ImageIcon
                sx={{ 
                  fontSize: { xs: 20, sm: 25, md: 30, lg: 30, xl: 30 }, 
                  color: "gray", 
                  padding: { xs: "20px", sm: "35px", md: "50px", lg: "50px", xl: "50px" } 
                }}
              />
            </Box>
          )}

          {headerComp && headerFormat === "VIDEO" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                borderBottom: "1px solid rgb(219, 219, 219)",
              }}
            >
              <VideocamIcon
                sx={{ 
                  fontSize: { xs: 20, sm: 25, md: 30, lg: 30, xl: 30 }, 
                  color: "gray", 
                  padding: { xs: "20px", sm: "35px", md: "50px", lg: "50px", xl: "50px" } 
                }}
              />
            </Box>
          )}

          {headerComp && headerFormat === "DOCUMENT" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                borderBottom: "1px solid rgb(219, 219, 219)",
              }}
            >
              <PictureAsPdfIcon
                sx={{ 
                  fontSize: { xs: 20, sm: 25, md: 30, lg: 30, xl: 30 }, 
                  color: "gray", 
                  padding: { xs: "20px", sm: "35px", md: "50px", lg: "50px", xl: "50px" } 
                }}
              />
            </Box>
          )}

          {/* Body */}
          {bodyComp && (
            <Typography 
              variant="body2" 
              sx={{ 
                padding: { xs: "4px 0 0 6px", sm: "4px 0 0 8px", md: "5px 0 0 10px", lg: "5px 0 0 10px", xl: "5px 0 0 10px" },
                fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
              }}
            >
              {bodyText}
            </Typography>
          )}

          {/* Footer */}
          {footerComp && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ 
                display: "block", 
                padding: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
                fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px", xl: "12px" }
              }}
            >
              {footerText}
            </Typography>
          )}

          <Divider />

          {/* Carousel Component */}
          {carouselComp && carouselCards.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 1,
                }}
              >
                <ViewCarouselIcon
                  sx={{
                    fontSize: { xs: 20, sm: 25, md: 30, lg: 30, xl: 30 },
                    color: "#2F8D6B",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#2F8D6B",
                    fontWeight: "medium",
                    ml: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
                    fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
                  }}
                >
                  Carousel ({carouselCards.length} cards)
                </Typography>
              </Box>
              <Divider />
            </>
          )}

          {/* Buttons Section */}
          {buttonsData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
                padding: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
              }}
            >
              {buttonsData.map((btn, index) => {
                // Each button has a type. Check which type it is and render accordingly:
                switch (btn.type?.toUpperCase()) {
                  case "QUICK_REPLY":
                    return (
                      <Typography
                        key={index}
                        sx={{
                          color: "#2F8D6B",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
                        }}
                      >
                        <ReplyIcon sx={{ marginRight: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 }, fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" } }} />
                        {btn.text || "Quick Reply"}
                      </Typography>
                    );

                  case "PHONE_NUMBER":
                    // Example: label or text for phone button
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#2F8D6B",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
                          }}
                        >
                          <CallIcon sx={{ marginRight: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 }, fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" } }} />
                          {btn.text || "Call Button"}
                        </Typography>
                      </Box>
                    );

                  case "URL":
                    // Example: label or text for link button
                    return (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: "#2F8D6B",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
                          fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
                        }}
                      >
                        <LaunchIcon sx={{ marginRight: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 }, fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" } }} />
                        {btn.text || "Link Button"}
                      </Typography>
                    );

                  case "COPY_CODE":
                    // Example: label or text for copy-code button
                    return (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: "#2F8D6B",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
                          fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" }
                        }}
                      >
                        <ContentCopyIcon sx={{ marginRight: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 }, fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" } }} />
                        {btn.text || "Copy Code Button"}
                      </Typography>
                    );

                  default:
                    // Fallback for unknown button types
                    return (
                      <Typography key={index} sx={{ textAlign: "center" }}>
                        {btn.text || "Button"}
                      </Typography>
                    );
                }
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeamplateSendPreview;
